import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XCircleIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { Heading, Text, Button, Input, Select, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import { modulesService } from '../../services/modules'
import { marketingService } from '../../services/marketing'
import { BRAND_COLOR } from '../../constants'
import type {
  MarketingClipping,
  MarketingClippingCatalogItem,
  MarketingClippingCategory,
  MarketingClippingSearchConfig,
  MarketingClippingOrigin,
  MarketingClippingVehicle,
} from '../../types/marketing'
import ClippingDashboardTab from './ClippingDashboardTab'
import ClippingDetalheModal from './ClippingDetalheModal'

/** Suprime o texto padrão "Carregando permissões..." — a tela já exibe loading próprio dos dados. */
const CLIPPING_PERM_LOADING: React.ReactNode = null
const MODULE_NAME = 'marketing_clipping'

type ActiveTab = 'relacao' | 'dashboard'

const tabClass = (activeTab: ActiveTab, tab: ActiveTab) =>
  `py-3 px-1 border-b-2 font-medium text-sm ${
    activeTab === tab
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`

type CatalogKind = 'veiculo' | 'categoria'

interface CatalogSelectTarget {
  kind: CatalogKind
  context: 'clipping'
  vehicleRowKey?: string
}

interface ClippingFormItemRow {
  key: string
  vehicle_id: string
  link: string
}

interface ClippingFormState {
  id?: string
  title: string
  clipping_date: string
  category_id: string
  notes: string
  items: ClippingFormItemRow[]
}

const newItemRow = (): ClippingFormItemRow => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  vehicle_id: '',
  link: '',
})

const emptyForm = (): ClippingFormState => ({
  title: '',
  clipping_date: new Date().toISOString().slice(0, 10),
  category_id: '',
  notes: '',
  items: [newItemRow()],
})

function formatDateBr(value: string): string {
  if (!value) return '—'
  const [y, m, d] = value.slice(0, 10).split('-')
  if (!y || !m || !d) return value
  return `${d}/${m}/${y}`
}

function originLabel(origin: MarketingClippingOrigin): string {
  return origin === 'ia' ? 'IA' : 'Manual'
}

function catalogTitle(kind: CatalogKind): string {
  if (kind === 'veiculo') return 'Veículos'
  return 'Categorias'
}

function catalogEntityLabel(kind: CatalogKind): string {
  if (kind === 'veiculo') return 'veículo'
  return 'categoria'
}

function catalogLogType(kind: CatalogKind) {
  if (kind === 'veiculo') return 'marketing_clipping_vehicle' as const
  return 'marketing_clipping_category' as const
}

function sortCatalog<T extends MarketingClippingCatalogItem>(list: T[]): T[] {
  return [...list].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
}

function snapshotClipping(item: {
  title: string
  clipping_date: string
  category_id: string
  notes?: string | null
  origin?: MarketingClippingOrigin
  search_config_id?: string | null
  items?: Array<{ vehicle_id: string; link: string }>
}) {
  return {
    title: item.title,
    clipping_date: item.clipping_date,
    category_id: item.category_id,
    notes: item.notes ?? null,
    origin: item.origin ?? null,
    search_config_id: item.search_config_id ?? null,
    items: (item.items || []).map((i) => ({ vehicle_id: i.vehicle_id, link: i.link })),
  }
}

const MarketingClippingPage: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()

  const [moduleTitle, setModuleTitle] = useState('Clipping')
  const [moduleDescription, setModuleDescription] = useState(
    'Cadastro e gestão de clippings de marketing'
  )

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [activeTab, setActiveTab] = useState<ActiveTab>('relacao')
  const [filterQuery, setFilterQuery] = useState('')
  const [filterDataInicio, setFilterDataInicio] = useState('')
  const [filterDataFim, setFilterDataFim] = useState('')
  const [filterVeiculoId, setFilterVeiculoId] = useState('')
  const [filterCategoriaId, setFilterCategoriaId] = useState('')
  const [filterOrigin, setFilterOrigin] = useState<'' | MarketingClippingOrigin>('')
  const [filterSearchConfigId, setFilterSearchConfigId] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)

  const [items, setItems] = useState<MarketingClipping[]>([])
  const [veiculos, setVeiculos] = useState<MarketingClippingVehicle[]>([])
  const [categorias, setCategorias] = useState<MarketingClippingCategory[]>([])
  const [searchConfigs, setSearchConfigs] = useState<MarketingClippingSearchConfig[]>([])

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [detailClipping, setDetailClipping] = useState<MarketingClipping | null>(null)
  const [form, setForm] = useState<ClippingFormState>(emptyForm())
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const [catalogModal, setCatalogModal] = useState<CatalogKind | null>(null)
  const [catalogSelectTarget, setCatalogSelectTarget] = useState<CatalogSelectTarget | null>(null)
  const [catalogName, setCatalogName] = useState('')
  const [catalogEditingId, setCatalogEditingId] = useState<string | null>(null)
  const [catalogSaving, setCatalogSaving] = useState(false)
  const [catalogTogglingId, setCatalogTogglingId] = useState<string | null>(null)
  const [catalogError, setCatalogError] = useState<string | null>(null)

  useEffect(() => {
    modulesService.getByName(MODULE_NAME).then((result) => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [])

  const loadAll = useCallback(async (opts?: { showOverlay?: boolean }) => {
    const showOverlay = opts?.showOverlay ?? true
    try {
      if (showOverlay) setLoading(true)
      setError(null)
      const [clippings, veiculosData, categoriasData, searchConfigsData] =
        await Promise.all([
          marketingService.listClippings(),
          marketingService.listVehicles(false),
          marketingService.listCategories(false),
          marketingService.listSearchConfigs(),
        ])
      setItems(clippings)
      setVeiculos(veiculosData)
      setCategorias(categoriasData)
      setSearchConfigs(
        [...searchConfigsData].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      )
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao carregar clippings'
      setError(message)
    } finally {
      if (showOverlay) setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const hasActiveFilters = Boolean(
    filterQuery.trim() ||
      filterDataInicio ||
      filterDataFim ||
      filterVeiculoId ||
      filterCategoriaId ||
      filterOrigin ||
      filterSearchConfigId
  )

  const clearFilters = () => {
    setFilterQuery('')
    setFilterDataInicio('')
    setFilterDataFim('')
    setFilterVeiculoId('')
    setFilterCategoriaId('')
    setFilterOrigin('')
    setFilterSearchConfigId('')
  }

  const searchConfigNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const config of searchConfigs) {
      map.set(config.id, config.name)
    }
    return map
  }, [searchConfigs])

  const resolveSearchConfigName = (item: MarketingClipping): string | null => {
    if (item.search_config?.name) return item.search_config.name
    if (item.search_config_id) {
      return searchConfigNameById.get(item.search_config_id) || null
    }
    return null
  }

  const filtered = useMemo(() => {
    const q = filterQuery.trim().toLowerCase()
    return items.filter((item) => {
      const itemDate = item.clipping_date?.slice(0, 10) || ''
      const clippingItems = item.items || []
      const searchConfigName = resolveSearchConfigName(item)

      if (q) {
        const haystack = [
          item.title,
          item.notes,
          searchConfigName,
          ...clippingItems.map((i) => i.link),
          ...clippingItems.map((i) => i.vehicle?.name || ''),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(q)) return false
      }
      if (filterDataInicio && itemDate < filterDataInicio) return false
      if (filterDataFim && itemDate > filterDataFim) return false
      if (filterVeiculoId && !clippingItems.some((i) => i.vehicle_id === filterVeiculoId)) return false
      if (filterCategoriaId && item.category_id !== filterCategoriaId) return false
      if (filterOrigin && item.origin !== filterOrigin) return false
      if (filterSearchConfigId && item.search_config_id !== filterSearchConfigId) return false
      return true
    })
  }, [
    items,
    filterQuery,
    filterDataInicio,
    filterDataFim,
    filterVeiculoId,
    filterCategoriaId,
    filterOrigin,
    filterSearchConfigId,
    searchConfigNameById,
  ])

  useEffect(() => {
    setCurrentPage(1)
  }, [
    filterQuery,
    filterDataInicio,
    filterDataFim,
    filterVeiculoId,
    filterCategoriaId,
    filterOrigin,
    filterSearchConfigId,
  ])

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage) || 1)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedItems = filtered.slice(startIndex, endIndex)

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1))
  }

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
  }

  const openNew = () => {
    setForm(emptyForm())
    setValidationErrors({})
    setIsModalOpen(true)
  }

  const openEdit = (item: MarketingClipping) => {
    const clippingItems =
      item.items && item.items.length > 0
        ? item.items.map((i) => ({
            key: i.id,
            vehicle_id: i.vehicle_id,
            link: i.link,
          }))
        : [newItemRow()]
    setForm({
      id: item.id,
      title: item.title,
      clipping_date: item.clipping_date?.slice(0, 10) || '',
      category_id: item.category_id,
      notes: item.notes || '',
      items: clippingItems,
    })
    setValidationErrors({})
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setForm(emptyForm())
    setValidationErrors({})
  }

  const updateItemRow = (key: string, patch: Partial<ClippingFormItemRow>) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    }))
  }

  const addItemRow = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, newItemRow()] }))
  }

  const removeItemRow = (key: string) => {
    setForm((prev) => {
      if (prev.items.length <= 1) return prev
      return { ...prev, items: prev.items.filter((row) => row.key !== key) }
    })
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    if (!form.title.trim()) errors.title = 'Informe o título'
    if (!form.clipping_date) errors.clipping_date = 'Informe a data'
    if (!form.category_id) errors.category_id = 'Selecione a categoria'

    const validItems = form.items.filter((row) => row.vehicle_id && row.link.trim())
    if (validItems.length === 0) {
      errors.items = 'Adicione ao menos um veículo com link'
    } else {
      const incomplete = form.items.some(
        (row) => (row.vehicle_id && !row.link.trim()) || (!row.vehicle_id && row.link.trim())
      )
      if (incomplete) {
        errors.items = 'Cada linha deve ter veículo e link preenchidos'
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const save = async () => {
    if (!validateForm()) return
    const itemsPayload = form.items
      .filter((row) => row.vehicle_id && row.link.trim())
      .map((row) => ({
        vehicle_id: row.vehicle_id,
        link: row.link.trim(),
      }))

    const notes = form.notes.trim() || null

    try {
      setSaving(true)
      setError(null)
      if (form.id) {
        const previous = items.find((i) => i.id === form.id)
        const updated = await marketingService.updateClipping({
          id: form.id,
          title: form.title.trim(),
          clipping_date: form.clipping_date,
          category_id: form.category_id,
          notes,
          items: itemsPayload,
        })
        void logUpdate(
          'marketing_clipping',
          updated.id,
          `Atualizou clipping: ${updated.title}`,
          previous ? snapshotClipping(previous) : undefined,
          snapshotClipping({
            ...updated,
            items: itemsPayload,
          })
        )
      } else {
        const created = await marketingService.createClipping({
          title: form.title.trim(),
          clipping_date: form.clipping_date,
          category_id: form.category_id,
          notes,
          origin: 'manual',
          created_by: user?.id ?? null,
          items: itemsPayload,
        })
        void logCreate(
          'marketing_clipping',
          created.id,
          `Criou clipping: ${created.title}`,
          snapshotClipping(created)
        )
      }
      closeModal()
      await loadAll({ showOverlay: false })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar clipping'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  const remove = async (item: MarketingClipping) => {
    if (!window.confirm(`Excluir o clipping "${item.title}"?`)) return
    try {
      setError(null)
      await marketingService.deleteClipping(item.id, user?.id ?? null)
      void logDelete(
        'marketing_clipping',
        item.id,
        `Excluiu clipping: ${item.title}`,
        {
          ...snapshotClipping(item),
          additional_info: 'exclusao_logica',
        }
      )
      await loadAll({ showOverlay: false })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao excluir clipping'
      setError(message)
    }
  }

  const activeVeiculos = useMemo(() => veiculos.filter((v) => v.is_active), [veiculos])
  const activeCategorias = useMemo(() => categorias.filter((c) => c.is_active), [categorias])

  const catalogItems = useMemo((): MarketingClippingCatalogItem[] => {
    if (catalogModal === 'veiculo') return veiculos
    if (catalogModal === 'categoria') return categorias
    return []
  }, [catalogModal, veiculos, categorias])

  const openCatalogModal = (
    kind: CatalogKind,
    target?: Omit<CatalogSelectTarget, 'kind'> | null
  ) => {
    setCatalogModal(kind)
    setCatalogSelectTarget(target ? { kind, ...target } : null)
    setCatalogName('')
    setCatalogEditingId(null)
    setCatalogError(null)
  }

  const closeCatalogModal = () => {
    setCatalogModal(null)
    setCatalogSelectTarget(null)
    setCatalogName('')
    setCatalogEditingId(null)
    setCatalogError(null)
    setCatalogTogglingId(null)
  }

  const startEditCatalog = (item: MarketingClippingCatalogItem) => {
    setCatalogEditingId(item.id)
    setCatalogName(item.name)
    setCatalogError(null)
  }

  const cancelEditCatalog = () => {
    setCatalogEditingId(null)
    setCatalogName('')
    setCatalogError(null)
  }

  const saveCatalog = async () => {
    if (!catalogModal) return
    const name = catalogName.trim()
    if (!name) {
      setCatalogError('Informe o nome')
      return
    }
    try {
      setCatalogSaving(true)
      setCatalogError(null)

      if (catalogEditingId) {
        const previous = catalogItems.find((row) => row.id === catalogEditingId)
        let updated: MarketingClippingCatalogItem
        if (catalogModal === 'veiculo') {
          updated = await marketingService.updateVehicle(catalogEditingId, { name })
          setVeiculos((prev) => sortCatalog(prev.map((row) => (row.id === updated.id ? updated : row))))
        } else {
          updated = await marketingService.updateCategory(catalogEditingId, { name })
          setCategorias((prev) => sortCatalog(prev.map((row) => (row.id === updated.id ? updated : row))))
        }
        void logUpdate(
          catalogLogType(catalogModal),
          updated.id,
          `Atualizou ${catalogEntityLabel(catalogModal)} de clipping: ${updated.name}`,
          previous
            ? { name: previous.name, is_active: previous.is_active }
            : undefined,
          { name: updated.name, is_active: updated.is_active }
        )
        setCatalogEditingId(null)
        setCatalogName('')
        return
      }

      if (catalogModal === 'veiculo') {
        const created = await marketingService.createVehicle({ name })
        setVeiculos((prev) => sortCatalog([...prev, created]))
        const target = catalogSelectTarget
        if (target?.context === 'clipping' || (!target && isModalOpen)) {
          const rowKey = target?.vehicleRowKey
          setForm((prev) => {
            if (rowKey) {
              return {
                ...prev,
                items: prev.items.map((row) =>
                  row.key === rowKey ? { ...row, vehicle_id: created.id } : row
                ),
              }
            }
            const emptyIdx = prev.items.findIndex((row) => !row.vehicle_id)
            if (emptyIdx >= 0) {
              return {
                ...prev,
                items: prev.items.map((row, idx) =>
                  idx === emptyIdx ? { ...row, vehicle_id: created.id } : row
                ),
              }
            }
            return prev
          })
        }
        void logCreate(
          catalogLogType('veiculo'),
          created.id,
          `Criou veículo de clipping: ${created.name}`,
          { name: created.name, is_active: created.is_active }
        )
      } else {
        const created = await marketingService.createCategory({ name })
        setCategorias((prev) => sortCatalog([...prev, created]))
        const target = catalogSelectTarget
        if (target?.context === 'clipping' || (!target && isModalOpen)) {
          setForm((prev) => ({ ...prev, category_id: created.id }))
        }
        void logCreate(
          catalogLogType('categoria'),
          created.id,
          `Criou categoria de clipping: ${created.name}`,
          { name: created.name, is_active: created.is_active }
        )
      }
      setCatalogSelectTarget(null)
      setCatalogName('')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar'
      setCatalogError(message)
    } finally {
      setCatalogSaving(false)
    }
  }

  const toggleCatalogActive = async (item: MarketingClippingCatalogItem) => {
    if (!catalogModal) return
    const nextActive = !item.is_active
    const label = catalogEntityLabel(catalogModal)
    try {
      setCatalogTogglingId(item.id)
      setCatalogError(null)
      let updated: MarketingClippingCatalogItem
      if (catalogModal === 'veiculo') {
        updated = await marketingService.updateVehicle(item.id, { is_active: nextActive })
        setVeiculos((prev) => sortCatalog(prev.map((row) => (row.id === updated.id ? updated : row))))
      } else {
        updated = await marketingService.updateCategory(item.id, { is_active: nextActive })
        setCategorias((prev) => sortCatalog(prev.map((row) => (row.id === updated.id ? updated : row))))
      }
      void logUpdate(
        catalogLogType(catalogModal),
        updated.id,
        `${nextActive ? 'Ativou' : 'Inativou'} ${label} de clipping: ${updated.name}`,
        { name: item.name, is_active: item.is_active },
        { name: updated.name, is_active: updated.is_active }
      )
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao atualizar status'
      setCatalogError(message)
    } finally {
      setCatalogTogglingId(null)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          loadingComponent={CLIPPING_PERM_LOADING}
        >
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                  {moduleTitle}
                </Heading>
                <Text size="sm" color="muted" className="mt-2">
                  {moduleDescription}
                </Text>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <PermissionGuard moduleName={MODULE_NAME} permissionType="insert" fallback={null} loadingComponent={CLIPPING_PERM_LOADING}>
                  <Button variant="solid" onClick={openNew} className="flex items-center gap-1">
                    <PlusIcon className="h-5 w-5" />
                    Cadastrar clipping
                  </Button>
                </PermissionGuard>
              </div>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            <div className="mb-4 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Filtros
                </Heading>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Buscar
                  </Text>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      className="pl-9"
                      placeholder="Título ou link..."
                      value={filterQuery}
                      onChange={(e) => setFilterQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Data inicial
                  </Text>
                  <Input
                    type="date"
                    value={filterDataInicio}
                    onChange={(e) => setFilterDataInicio(e.target.value)}
                  />
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Data final
                  </Text>
                  <Input
                    type="date"
                    value={filterDataFim}
                    onChange={(e) => setFilterDataFim(e.target.value)}
                  />
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Origem
                  </Text>
                  <Select
                    value={filterOrigin}
                    onChange={(e) => setFilterOrigin(e.target.value as '' | MarketingClippingOrigin)}
                  >
                    <option value="">Todas</option>
                    <option value="manual">Manual</option>
                    <option value="ia">IA</option>
                  </Select>
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Veículo
                  </Text>
                  <Select
                    value={filterVeiculoId}
                    onChange={(e) => setFilterVeiculoId(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {veiculos.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.is_active ? v.name : `${v.name} (inativo)`}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Categoria
                  </Text>
                  <Select
                    value={filterCategoriaId}
                    onChange={(e) => setFilterCategoriaId(e.target.value)}
                  >
                    <option value="">Todas</option>
                    {categorias.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.is_active ? c.name : `${c.name} (inativo)`}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block whitespace-nowrap">
                    Configuração de Busca
                  </Text>
                  <Select
                    value={filterSearchConfigId}
                    onChange={(e) => setFilterSearchConfigId(e.target.value)}
                  >
                    <option value="">Todas</option>
                    {searchConfigs.map((config) => (
                      <option key={config.id} value={config.id}>
                        {config.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                <Text size="sm" color="muted">
                  {filtered.length} registro{filtered.length === 1 ? '' : 's'}
                  {hasActiveFilters ? ' com os filtros aplicados' : ''}
                </Text>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  className="flex items-center gap-1"
                >
                  <XCircleIcon className="h-4 w-4" />
                  Limpar filtros
                </Button>
              </div>
            </div>

            <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8">
                <button
                  type="button"
                  onClick={() => setActiveTab('relacao')}
                  className={tabClass(activeTab, 'relacao')}
                >
                  Relação completa
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('dashboard')}
                  className={tabClass(activeTab, 'dashboard')}
                >
                  Dashboard
                </button>
              </nav>
            </div>

            {activeTab === 'dashboard' && (
              <ClippingDashboardTab clippings={filtered} loading={loading} />
            )}

            {activeTab === 'relacao' && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/30">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Título</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Data</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Categoria</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Origem</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                          Configuração de Busca
                        </th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {loading ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-10 text-center">
                            <LoadingSpinner />
                          </td>
                        </tr>
                      ) : filtered.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                            {hasActiveFilters
                              ? 'Nenhum clipping encontrado com os filtros aplicados.'
                              : 'Nenhum clipping encontrado.'}
                          </td>
                        </tr>
                      ) : (
                        paginatedItems.map((item) => (
                          <tr
                            key={item.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                            onClick={() => setDetailClipping(item)}
                          >
                            <td
                              className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium"
                              title={item.title}
                            >
                              {item.title.length > 30 ? `${item.title.slice(0, 30)}…` : item.title}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                              {formatDateBr(item.clipping_date)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.category?.name || '—'}</td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={
                                  item.origin === 'ia'
                                    ? 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300'
                                    : 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                }
                              >
                                {originLabel(item.origin)}
                              </span>
                            </td>
                            <td
                              className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap"
                              title={resolveSearchConfigName(item) || undefined}
                            >
                              {resolveSearchConfigName(item) || '—'}
                            </td>
                            <td
                              className="px-4 py-3 text-right whitespace-nowrap space-x-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <PermissionGuard moduleName={MODULE_NAME} permissionType="update" fallback={null} loadingComponent={CLIPPING_PERM_LOADING}>
                                <Button variant="outline" size="sm" onClick={() => openEdit(item)} title="Editar">
                                  <PencilIcon className="h-4 w-4" />
                                </Button>
                              </PermissionGuard>
                              <PermissionGuard moduleName={MODULE_NAME} permissionType="delete" fallback={null} loadingComponent={CLIPPING_PERM_LOADING}>
                                <Button variant="ghost" size="sm" onClick={() => remove(item)} title="Excluir">
                                  <TrashIcon className="h-4 w-4 text-red-500" />
                                </Button>
                              </PermissionGuard>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {!loading && filtered.length > 0 && (
                  <div className="px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300">
                        Mostrando {startIndex + 1} a {Math.min(endIndex, filtered.length)} de{' '}
                        {filtered.length} registros
                      </Text>
                      {totalPages > 1 && (
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Anterior
                          </button>
                          <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNumber: number
                              if (totalPages <= 5) {
                                pageNumber = i + 1
                              } else if (currentPage <= 3) {
                                pageNumber = i + 1
                              } else if (currentPage >= totalPages - 2) {
                                pageNumber = totalPages - 4 + i
                              } else {
                                pageNumber = currentPage - 2 + i
                              }
                              return (
                                <button
                                  key={pageNumber}
                                  type="button"
                                  onClick={() => handlePageChange(pageNumber)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                                    currentPage === pageNumber
                                      ? 'bg-primary-600 text-white'
                                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                  }`}
                                >
                                  {pageNumber}
                                </button>
                              )
                            })}
                          </div>
                          <button
                            type="button"
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Próximo
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {detailClipping && (
              <ClippingDetalheModal
                clipping={detailClipping}
                searchConfigName={resolveSearchConfigName(detailClipping)}
                onClose={() => setDetailClipping(null)}
              />
            )}

            {/* Modal clipping */}
            {isModalOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                  <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      {form.id ? 'Editar clipping' : 'Cadastrar clipping'}
                    </Heading>
                    <Button
                      onClick={closeModal}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1"
                      disabled={saving}
                    >
                      <XMarkIcon className="h-4 w-4" />
                      Fechar
                    </Button>
                  </div>

                  <div className="p-6 overflow-y-auto flex-1">
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-2">
                          <Text size="sm" className="mb-2">
                            Título <span className="text-red-500">*</span>
                          </Text>
                          <Input
                            value={form.title}
                            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                            placeholder="Digite o título do clipping..."
                            className={validationErrors.title ? 'border-red-500' : ''}
                          />
                          {validationErrors.title && (
                            <Text size="sm" className="text-red-500 mt-1">{validationErrors.title}</Text>
                          )}
                        </div>

                        <div>
                          <Text size="sm" className="mb-2">
                            Data <span className="text-red-500">*</span>
                          </Text>
                          <Input
                            type="date"
                            value={form.clipping_date}
                            onChange={(e) => setForm((prev) => ({ ...prev, clipping_date: e.target.value }))}
                            className={validationErrors.clipping_date ? 'border-red-500' : ''}
                          />
                          {validationErrors.clipping_date && (
                            <Text size="sm" className="text-red-500 mt-1">{validationErrors.clipping_date}</Text>
                          )}
                        </div>
                      </div>

                      <div>
                        <Text size="sm" className="mb-2">
                          Categoria <span className="text-red-500">*</span>
                        </Text>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <Select
                              value={form.category_id}
                              onChange={(e) => setForm((prev) => ({ ...prev, category_id: e.target.value }))}
                            >
                              <option value="">Selecione...</option>
                              {activeCategorias.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                              {form.category_id && !activeCategorias.some((c) => c.id === form.category_id) && (
                                <option value={form.category_id}>
                                  {categorias.find((c) => c.id === form.category_id)?.name || 'Categoria inativa'} (inativo)
                                </option>
                              )}
                            </Select>
                          </div>
                          <PermissionGuard
                            moduleName={MODULE_NAME}
                            permissionType="insert"
                            fallback={null}
                            loadingComponent={CLIPPING_PERM_LOADING}
                          >
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => openCatalogModal('categoria', { context: 'clipping' })}
                              title="Cadastrar categoria"
                              className="shrink-0 px-2"
                            >
                              <PlusIcon className="h-4 w-4" />
                            </Button>
                          </PermissionGuard>
                        </div>
                        {validationErrors.category_id && (
                          <Text size="sm" className="text-red-500 mt-1">{validationErrors.category_id}</Text>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Text size="sm">
                            Veículos e links <span className="text-red-500">*</span>
                          </Text>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addItemRow}
                            className="flex items-center gap-1"
                          >
                            <PlusIcon className="h-4 w-4" />
                            Adicionar veículo
                          </Button>
                        </div>
                        <div className="space-y-3">
                          {form.items.map((row) => (
                            <div
                              key={row.key}
                              className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1.4fr_auto] gap-2 items-end"
                            >
                              <div>
                                <Text size="xs" className="mb-1 text-gray-500 dark:text-gray-400">
                                  Nome do veículo
                                </Text>
                                <Select
                                  value={row.vehicle_id}
                                  onChange={(e) => updateItemRow(row.key, { vehicle_id: e.target.value })}
                                >
                                  <option value="">Selecione...</option>
                                  {activeVeiculos.map((v) => (
                                    <option key={v.id} value={v.id}>{v.name}</option>
                                  ))}
                                  {row.vehicle_id && !activeVeiculos.some((v) => v.id === row.vehicle_id) && (
                                    <option value={row.vehicle_id}>
                                      {veiculos.find((v) => v.id === row.vehicle_id)?.name || 'Veículo inativo'} (inativo)
                                    </option>
                                  )}
                                </Select>
                              </div>
                              <PermissionGuard
                                moduleName={MODULE_NAME}
                                permissionType="insert"
                                fallback={null}
                                loadingComponent={CLIPPING_PERM_LOADING}
                              >
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    openCatalogModal('veiculo', {
                                      context: 'clipping',
                                      vehicleRowKey: row.key,
                                    })
                                  }
                                  title="Cadastrar veículo"
                                  className="shrink-0 px-2 mb-0.5"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                </Button>
                              </PermissionGuard>
                              <div>
                                <Text size="xs" className="mb-1 text-gray-500 dark:text-gray-400">
                                  Link
                                </Text>
                                <Input
                                  type="url"
                                  value={row.link}
                                  onChange={(e) => updateItemRow(row.key, { link: e.target.value })}
                                  placeholder="https://exemplo.com/noticia"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItemRow(row.key)}
                                disabled={form.items.length <= 1}
                                title="Remover"
                                className="mb-0.5"
                              >
                                <TrashIcon className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          ))}
                        </div>
                        {validationErrors.items && (
                          <Text size="sm" className="text-red-500 mt-2">{validationErrors.items}</Text>
                        )}
                      </div>

                      <div>
                        <Text size="sm" className="mb-2">
                          Observações
                        </Text>
                        <textarea
                          value={form.notes}
                          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                          placeholder="Opcional"
                          rows={4}
                          className="w-full px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-white/5 ring-1 ring-gray-300 dark:ring-white/10 rounded-lg shadow-sm placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500/20"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                    <Button onClick={closeModal} variant="outline" disabled={saving}>
                      Cancelar
                    </Button>
                    <Button
                      onClick={save}
                      disabled={
                        saving ||
                        !form.title.trim() ||
                        !form.clipping_date ||
                        !form.category_id ||
                        !form.items.some((row) => row.vehicle_id && row.link.trim())
                      }
                      className="flex items-center gap-2"
                      style={{ backgroundColor: BRAND_COLOR }}
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="h-4 w-4" />
                          {form.id ? 'Atualizar' : 'Criar'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal catálogo */}
            {catalogModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col">
                  <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      {catalogTitle(catalogModal)}
                    </Heading>
                    <Button
                      onClick={closeCatalogModal}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1"
                      disabled={catalogSaving || Boolean(catalogTogglingId)}
                    >
                      <XMarkIcon className="h-4 w-4" />
                      Fechar
                    </Button>
                  </div>

                  <div className="p-6 overflow-y-auto flex-1 space-y-6">
                    <div>
                      <Text size="sm" className="mb-2">
                        {catalogEditingId ? 'Editar registro' : 'Novo registro'}
                      </Text>
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <Input
                            value={catalogName}
                            onChange={(e) => setCatalogName(e.target.value)}
                            placeholder="Digite o nome..."
                            className={catalogError ? 'border-red-500' : ''}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                void saveCatalog()
                              }
                            }}
                          />
                        </div>
                        {catalogEditingId ? (
                          <>
                            <PermissionGuard moduleName={MODULE_NAME} permissionType="update" fallback={null} loadingComponent={CLIPPING_PERM_LOADING}>
                              <Button
                                onClick={saveCatalog}
                                disabled={catalogSaving || !catalogName.trim()}
                                className="flex items-center gap-1 shrink-0"
                                style={{ backgroundColor: BRAND_COLOR }}
                              >
                                {catalogSaving ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                ) : (
                                  <CheckIcon className="h-4 w-4" />
                                )}
                                Atualizar
                              </Button>
                            </PermissionGuard>
                            <Button
                              variant="outline"
                              onClick={cancelEditCatalog}
                              disabled={catalogSaving}
                              className="shrink-0"
                            >
                              Cancelar
                            </Button>
                          </>
                        ) : (
                          <PermissionGuard moduleName={MODULE_NAME} permissionType="insert" fallback={null} loadingComponent={CLIPPING_PERM_LOADING}>
                            <Button
                              onClick={saveCatalog}
                              disabled={catalogSaving || !catalogName.trim()}
                              className="flex items-center gap-1 shrink-0"
                              style={{ backgroundColor: BRAND_COLOR }}
                            >
                              {catalogSaving ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                              ) : (
                                <PlusIcon className="h-4 w-4" />
                              )}
                              Adicionar
                            </Button>
                          </PermissionGuard>
                        )}
                      </div>
                    </div>

                    {catalogError && (
                      <Text size="sm" className="text-red-500">{catalogError}</Text>
                    )}

                    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900/30">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Nome</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                            <th className="px-4 py-3" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                          {catalogItems.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                Nenhum registro cadastrado.
                              </td>
                            </tr>
                          ) : (
                            catalogItems.map((item) => (
                              <tr
                                key={item.id}
                                className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                                  catalogEditingId === item.id
                                    ? 'bg-primary-50/60 dark:bg-primary-900/20'
                                    : ''
                                }`}
                              >
                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-medium">
                                  {item.name}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span
                                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                      item.is_active
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                                        : 'bg-gray-100 text-gray-600 dark:bg-gray-900/30 dark:text-gray-300'
                                    }`}
                                  >
                                    {item.is_active ? 'Ativo' : 'Inativo'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap space-x-2">
                                  <PermissionGuard moduleName={MODULE_NAME} permissionType="update" fallback={null} loadingComponent={CLIPPING_PERM_LOADING}>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => startEditCatalog(item)}
                                      disabled={catalogSaving || Boolean(catalogTogglingId)}
                                      title="Editar"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </Button>
                                  </PermissionGuard>
                                  <PermissionGuard moduleName={MODULE_NAME} permissionType="update" fallback={null} loadingComponent={CLIPPING_PERM_LOADING}>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => void toggleCatalogActive(item)}
                                      disabled={catalogTogglingId === item.id || catalogSaving}
                                    >
                                      {catalogTogglingId === item.id
                                        ? 'Salvando...'
                                        : item.is_active
                                          ? 'Inativar'
                                          : 'Ativar'}
                                    </Button>
                                  </PermissionGuard>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </PermissionGuard>
      </div>
  )
}

export default MarketingClippingPage
